import React, { useState } from "react";
import { Card, Group, Text, Badge, Code, Collapse, Button, Tooltip } from "@mantine/core";
import { Submission } from "types/userTypes";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { requestFetchSubmissions } from "@actions/userActions";
import { formatDateAndTime } from "@utils/CommonUtil";
import { getSubmissionTypeLabel } from "@utils/UIUtils";
import {
  cancelSubmission as cancelSubmissionRequest,
  updateOrDenySubmissionRequest,
} from "@utils/api-communication/RestApiCli";

export interface SubmittedRequestItemProps {
  submission: Submission;
  role: string;
}

export const SubmissionItem: React.FC<SubmittedRequestItemProps> = ({ submission, role }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [expanded, setExpanded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const formattedDate = submission?.createdAt ? formatDateAndTime(submission.createdAt) : "";
  const isAdmin = role === "admin";
  const isPending = !submission.isApproved;

  const handleApprove = async () => {
    setIsProcessing(true);
    try {
      await new Promise((resolve, reject) => {
        updateOrDenySubmissionRequest(
          { submissionId: submission._id, isApproved: true },
          resolve,
          reject,
        );
      });
      dispatch(requestFetchSubmissions());
    } catch (error) {
      console.error("Failed to approve submission:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeny = async () => {
    setIsProcessing(true);
    try {
      await new Promise((resolve, reject) => {
        updateOrDenySubmissionRequest(
          { submissionId: submission._id, isApproved: false },
          resolve,
          reject,
        );
      });
      dispatch(requestFetchSubmissions());
    } catch (error) {
      console.error("Failed to deny submission:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = async () => {
    setExpanded(false);
    setIsProcessing(true);
    try {
      await new Promise((resolve, reject) => {
        cancelSubmissionRequest(submission._id, resolve, reject);
      });
      dispatch(requestFetchSubmissions());
    } catch (error) {
      console.error("Failed to cancel submission:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card withBorder radius="md" shadow="xs">
      <Group gap="xs" justify="space-between">
        <Group gap="xs">
          <Badge size="sm" variant="dot" color={submission.isApproved ? "green" : "yellow"}>
            {t(getSubmissionTypeLabel(submission.type))}
          </Badge>
          <Text size="xs" color="dimmed" truncate>
            {t("submissions.from")} <Code>{submission.distId}</Code> {formattedDate}
          </Text>
        </Group>
        <Group gap="xs">
          {isAdmin && isPending && (
            <Group>
              <Button
                color="red"
                onClick={handleDeny}
                loading={isProcessing}
                disabled={isProcessing}
                size="xs"
                aria-label={t("submissions.reject")}
              >
                {t("submissions.reject")}
              </Button>
              <Button
                color="green"
                onClick={handleApprove}
                loading={isProcessing}
                disabled={isProcessing}
                size="xs"
                aria-label={t("submissions.accept")}
              >
                {t("submissions.accept")}
              </Button>
            </Group>
          )}
          {isPending && (
            <Group>
              <Button
                variant="outline"
                color="red"
                onClick={handleCancel}
                loading={isProcessing}
                disabled={isProcessing}
                size="xs"
                aria-label={t("submissions.cancel")}
              >
                {t("submissions.cancel")}
              </Button>
            </Group>
          )}
          <Tooltip label={expanded ? "Show less" : "Show details"}>
            <Button variant="subtle" onClick={() => setExpanded(!expanded)} px={4}>
              {expanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
            </Button>
          </Tooltip>
        </Group>
      </Group>

      <Collapse in={expanded}>
        <Card.Section pt="xs">
          <Text size="xs" fw={500} mx="xs">
            {t("submissions.details")}
          </Text>
          <Code block style={{ fontSize: "11px" }}>
            {JSON.stringify(submission.payload, null, 2)}
          </Code>
        </Card.Section>
      </Collapse>
    </Card>
  );
};
